import React from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { css } from '@emotion/core';
import MarketplaceSelector from './MarketplaceSelector';
import LocationSelector from './LocationSelector';
import ENUMS from '../../../constants/enums';
import AmazonConnect from '../../SettingTabs/APIConnect/AmazonConnect';

import {
  DestinationResultContainer,
  DestinationResultText,
  ConnectAnother,
  ConnectAccount,
  CloseButton,
  nonApiLocation,
  nonApiMarketplace,
  apiMarketplace,
  apiLocation,
  Indication4,
} from './styled';
import IconSvg from '../../common/IconSvg';
import NonApiConnection from './NonApiConnection';

@inject('ebayOAuthConnection')
@inject('azdConnection')
@inject('onboardingStore')
@observer
class DestinationConnector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      amazonConnectionMode: false,
      connectButtonPressed: false,
      amazonSuccess: false,
      connectAnotherNonApi: false,
      nonApiConnectionErrorMessage: '',
      locationName: '',
    };
  }

  componentDidMount() {
    this.props.ebayOAuthConnection.reinit();
    this.props.ebayOAuthConnection.setMarketplace(ENUMS.MARKETS.EBAY);
    this.props.ebayOAuthConnection.setLocation('UNITED_STATES');
  }

  setAmazonConnectionMode = val => {
    this.setState({
      amazonConnectionMode: val,
    });
  };

  setAmazonSuccess = val => {
    this.setState({
      amazonSuccess: val,
    });
  };

  closeAction = () => {
    this.setState({
      connectButtonPressed: false,
      amazonConnectionMode: false,
    });
  };

  // this should only check for info being set when other destinations will be supported
  validateChoice = () => {
    const { marketplace, destinationUrl, connectAccount } = this.props.ebayOAuthConnection;
    if (marketplace === ENUMS.MARKETS.EBAY) {
      if (!destinationUrl) {
        connectAccount();
      } else {
        return true;
      }
    } else if (marketplace === ENUMS.MARKETS.AMAZON) {
      return true;
    }
    return false;
  };

  // this should also add the choice to the store when other destinations will be supported
  handleMarketplace = choice => {
    this.props.ebayOAuthConnection.reinit();
    this.props.ebayOAuthConnection.setLocation('UNITED_STATES');
    this.props.ebayOAuthConnection.setMarketplace(choice);
  };

  // this should also add the choice to the store when other destinations will be supported
  handleLocation = choice => {
    this.setState({ locationName: choice });
    this.props.ebayOAuthConnection.setLocation(choice);
  };

  openConnectWindow = () => {
    const { connectToEbay } = this.props.ebayOAuthConnection;
    // if(!destinationUrl.includes('prompt=login')){
    //   setDestinationUrl(`${destinationUrl}&prompt=login`);
    // }
    connectToEbay();
  };

  handleConnection = () => {
    const {
      ebayOAuthConnection: { marketplace },
      azdConnection: { getDeveloperData },
    } = this.props;
    switch (marketplace) {
      case ENUMS.MARKETS.EBAY: {
        this.openConnectWindow();
        break;
      }
      case ENUMS.MARKETS.AMAZON: {
        this.setAmazonConnectionMode(true);
        this.setState({ connectButtonPressed: true });
        getDeveloperData();
        break;
      }
      default: {
        break;
      }
    }
  };

  handleNonApiConnection = (storeName, paypalName, NonApiToken) => {
    const {
      ebayOAuthConnection: {
        settingsStore: { connectionManagement, markets },
      },
      onboardingStore: { updateOnboardingLevel },
    } = this.props;
    connectionManagement.createNonApiAccount(storeName, paypalName, NonApiToken).then(result => {
      if (result.success === true) {
        this.setState({ connectAnotherNonApi: false, nonApiConnectionErrorMessage: '' });
        markets.fetchSellersAccounts();
        updateOnboardingLevel('has store');
      } else if (result.message === 'This store is already connected to another account.')
        this.setState({
          nonApiConnectionErrorMessage: 'This store is already connected to another account.',
        });
    });
  };

  resetNonApiConnectForm = () => {
    this.setState({ connectAnotherNonApi: true });
  };

  renderApiFlow() {
    const { amazonConnectionMode, connectButtonPressed, amazonSuccess, locationName } = this.state;
    const {
      ebayOAuthConnection: { firstAccountLoading, success, settingsStore },
      onboardingStore: { enableNextWizard, disableNextWizard, destinationAccountId },
      azdConnection: { developerData },
    } = this.props;

    const validChoice = this.validateChoice();

    if (destinationAccountId) {
      enableNextWizard();
    } else if ((success && !firstAccountLoading) || amazonSuccess) {
      settingsStore.markets.fetchSellersAccounts().then(() => {
        enableNextWizard();
      });
    } else {
      disableNextWizard();
    }

    const result = success ? (
      <React.Fragment>
        <IconSvg type="v-green" size={16} />{' '}
        <DestinationResultText success>
          <FormattedMessage id="onboarding.screen4.accountConnected" />
        </DestinationResultText>
      </React.Fragment>
    ) : (
      <React.Fragment>
        <DestinationResultText>
          <FormattedMessage id="onboarding.screen4.failed" />
        </DestinationResultText>
      </React.Fragment>
    );

    return amazonConnectionMode ? (
      <div style={{ margin: '20px 0 0 20%' }}>
        <AmazonConnect
          connectButtonPressed={connectButtonPressed}
          developerData={developerData}
          closeForm={() => {
            this.closeAction();
            this.setAmazonSuccess(true);
          }}
        />
        <CloseButton closeAction={this.closeAction} />
      </div>
    ) : (
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          justify-content: space-evenly;
        `}
      >
        <MarketplaceSelector
          handleChoice={this.handleMarketplace}
          formatCss={apiMarketplace}
          locationName={locationName}
        />
        {/* @todo onboarding uncomment this after we get more locations */}
        {/* {marketplace ? <LocationSelector handleChoice={this.handleLocation} /> : null} */}
        <LocationSelector handleChoice={this.handleLocation} formatCss={apiLocation} />
        {validChoice && firstAccountLoading ? (
          <ConnectAccount clickHandler={this.handleConnection} />
        ) : null}
        {!firstAccountLoading ? (
          <DestinationResultContainer hasAuto order={7}>
            {result}
            <br />
            <ConnectAnother clickHandler={this.openConnectWindow} />
          </DestinationResultContainer>
        ) : null}
      </div>
    );
  }

  renderNonApiFlow() {
    const {
      ebayOAuthConnection: { settingsStore },
      onboardingStore: { enableNextWizard, disableNextWizard, destinationAccountId },
    } = this.props;
    const { connectAnotherNonApi } = this.state;
    const hasStores = settingsStore.markets.sellersAccounts.length > 0;

    if (destinationAccountId) {
      enableNextWizard();
    } else {
      disableNextWizard();
    }

    // const downloadExtensionStyle=!hasStores || connectAnotherNonApi? `position:absolute;top:190px;right:85px;`:`display:flex; justify-content:center; align-items:center; margin-top:5px;`
    return (
      <div
        css={css`
          display: flex;
          justify-content: space-evenly;
          width: 100%;
          flex-wrap: wrap;
        `}
      >
        <MarketplaceSelector handleChoice={this.handleMarketplace} formatCss={nonApiMarketplace} />
        {/* @todo onboarding uncomment this after we get more locations */}
        {/* {marketplace ? <LocationSelector handleChoice={this.handleLocation} /> : null} */}
        <LocationSelector handleChoice={this.handleLocation} formatCss={nonApiLocation} />
        {!hasStores || connectAnotherNonApi ? (
          <NonApiConnection
            connectAccount={this.handleNonApiConnection}
            error={this.state.nonApiConnectionErrorMessage}
          />
        ) : null}
        {hasStores && !connectAnotherNonApi ? (
          <DestinationResultContainer hasAuto={false}>
            <Indication4 />
            {settingsStore.markets.sellersAccounts.map(sellerAccount => (
              <div
                key={`${sellerAccount.name}`}
                css={css`
                  text-align: center;
                `}
              >
                {sellerAccount.name}
              </div>
            ))}
            <div
              css={css`
                text-align: center;
              `}
            >
              <ConnectAnother clickHandler={this.resetNonApiConnectForm} />
            </div>
          </DestinationResultContainer>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      onboardingStore: { userProfile },
    } = this.props;

    if (userProfile.isNonApi) return this.renderNonApiFlow();

    return this.renderApiFlow();
  }
}

DestinationConnector.propTypes = {
  ebayOAuthConnection: PropTypes.object,
  onboardingStore: PropTypes.object,
  azdConnection: PropTypes.object,
};

export default DestinationConnector;
